import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStoreClubAuth } from '../../contexts/StoreClubAuthContext';
import { remindersAPI, weatherAlertsAPI, ordersAPI, lockerPickupAPI } from '../../services/storeClubAPI';
import ClubLayout from '../../components/StoreClub/ClubLayout';
import LauraChat from '../../components/StoreClub/LauraChat';

interface Reminder {
  id: number;
  product_name: string;
  estimated_empty_date: string;
  days_until_empty: number;
  message: string;
}

interface WeatherAlert {
  id: number;
  alert_type: string;
  severity: string;
  message: string;
  affected_animals: string;
  created_at: string;
}

interface Order {
  id: number;
  order_number: string;
  status: string;
  status_display: string;
  total: string;
  created_at: string;
}

interface LockerPickup {
  locker_number: number;
  locker_name: string;
  passcode: string;
  order_number: string;
  order_id: number;
  items_summary: string;
  assigned_at: string;
  total: string;
}

const ClubDashboard: React.FC = () => {
  const { member, logout } = useStoreClubAuth();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [alerts, setAlerts] = useState<WeatherAlert[]>([]);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [lockerPickup, setLockerPickup] = useState<LockerPickup | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [remindersData, alertsData, ordersData, lockerData] = await Promise.all([
        remindersAPI.getAll(),
        weatherAlertsAPI.getAll(),
        ordersAPI.getAll(),
        lockerPickupAPI.getActive(),
      ]);
      setReminders(remindersData.filter((r: Reminder) => r.days_until_empty <= 14));
      setAlerts(alertsData.filter((a: WeatherAlert) => !a.created_at));
      setRecentOrders(ordersData.slice(0, 3));
      if (lockerData.has_active_pickup) {
        setLockerPickup(lockerData.pickup);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLauraMessage = () => {
    if (lockerPickup) {
      return `Hey ${member?.name?.split(' ')[0]}! Your order is ready in Locker ${lockerPickup.locker_number}. Pick it up anytime - we're available 24/7!`;
    }
    if (alerts.length > 0) {
      return `Hey ${member?.name?.split(' ')[0]}! Heads up - there's a weather alert for your area. Make sure your animals are safe!`;
    }
    if (reminders.length > 0) {
      return `Welcome back, ${member?.name?.split(' ')[0]}! Looks like you might be running low on some feed. Want to stock up?`;
    }
    return `Hey ${member?.name?.split(' ')[0]}! Your animals are all set. Anything I can help you with today?`;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/club');
  };

  if (loading) {
    return (
      <ClubLayout>
        <div style={styles.loading}>Loading...</div>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout>
      <LauraChat message={getLauraMessage()} />

      <div style={styles.content}>
        {alerts.length > 0 && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Weather Alerts</h2>
            {alerts.map((alert) => (
              <div
                key={alert.id}
                style={{
                  ...styles.alertCard,
                  backgroundColor: alert.severity === 'emergency' ? '#ffebee' : '#fff3e0',
                  borderLeftColor: alert.severity === 'emergency' ? '#E73C3E' : '#ff9800',
                }}
              >
                <div style={styles.alertHeader}>
                  <span style={styles.alertType}>
                    {alert.alert_type === 'cold' ? '🥶' : '🥵'} {alert.severity.toUpperCase()}
                  </span>
                </div>
                <p style={styles.alertMessage}>{alert.message}</p>
                <span style={styles.alertAnimals}>Affects: {alert.affected_animals}</span>
              </div>
            ))}
          </div>
        )}

        {/* Locker Pickup Card - Prominent */}
        {lockerPickup && (
          <div style={styles.lockerPickupCard}>
            <div style={styles.lockerPickupHeader}>
              <span style={styles.lockerPickupIcon}>🔐</span>
              <span style={styles.lockerPickupTitle}>ORDER READY FOR PICKUP</span>
            </div>
            <div style={styles.lockerPickupBody}>
              <div style={styles.lockerInfo}>
                <span style={styles.lockerLabel}>Your order is in</span>
                <span style={styles.lockerNumber}>Locker #{lockerPickup.locker_number}</span>
              </div>
              <div style={styles.passcodeBox}>
                <span style={styles.passcodeLabel}>YOUR CODE</span>
                <span style={styles.passcodeValue}>{lockerPickup.passcode}</span>
                <span style={styles.passcodeNote}>(one-time use)</span>
              </div>
            </div>
            <div style={styles.lockerPickupFooter}>
              <span style={styles.lockerOrderNumber}>Order #{lockerPickup.order_number}</span>
              <span style={styles.lockerOrderItems}>{lockerPickup.items_summary}</span>
            </div>
            <a
              href="https://maps.google.com/?q=11264+SR+231+Kettle+Falls+WA+99141"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.directionsButton}
            >
              Get Directions
            </a>
          </div>
        )}

        {reminders.length > 0 && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Feed Reminders</h2>
            {reminders.map((reminder) => (
              <div key={reminder.id} style={styles.reminderCard}>
                <div style={styles.reminderInfo}>
                  <span style={styles.reminderProduct}>{reminder.product_name}</span>
                  <span style={styles.reminderDays}>
                    {reminder.days_until_empty <= 0
                      ? 'Running low now!'
                      : `~${reminder.days_until_empty} days left`}
                  </span>
                </div>
                <Link to="/club/shop" style={styles.reorderButton}>
                  Reorder
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* My Card Button - Prominent */}
        <Link to="/club/card" style={styles.myCardButton}>
          <div style={styles.myCardIcon}>💳</div>
          <div style={styles.myCardText}>
            <span style={styles.myCardTitle}>My Card</span>
            <span style={styles.myCardSubtitle}>Show at checkout</span>
          </div>
        </Link>

        <div style={styles.quickActions}>
          <Link to="/club/shop" style={styles.actionCard}>
            <div style={styles.actionIcon}>🛒</div>
            <span style={styles.actionLabel}>Shop</span>
          </Link>
          <Link to="/club/orders" style={styles.actionCard}>
            <div style={styles.actionIcon}>📦</div>
            <span style={styles.actionLabel}>Orders</span>
          </Link>
          <Link to="/club/farm" style={styles.actionCard}>
            <div style={styles.actionIcon}>🐾</div>
            <span style={styles.actionLabel}>My Farm</span>
          </Link>
          <Link to="/club/profile" style={styles.actionCard}>
            <div style={styles.actionIcon}>⚙️</div>
            <span style={styles.actionLabel}>Settings</span>
          </Link>
        </div>

        {recentOrders.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Recent Orders</h2>
              <Link to="/club/orders" style={styles.viewAllLink}>View All</Link>
            </div>
            {recentOrders.map((order) => (
              <Link
                key={order.id}
                to={`/club/orders/${order.id}`}
                style={styles.orderCard}
              >
                <div style={styles.orderInfo}>
                  <span style={styles.orderNumber}>#{order.order_number}</span>
                  <span style={styles.orderDate}>
                    {new Date(order.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div style={styles.orderRight}>
                  <span style={{
                    ...styles.orderStatus,
                    backgroundColor: getStatusColor(order.status),
                  }}>
                    {order.status_display}
                  </span>
                  <span style={styles.orderTotal}>${order.total}</span>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div style={styles.memberInfo}>
          <div style={styles.memberCard}>
            <div style={styles.memberAvatar}>
              {member?.name?.charAt(0).toUpperCase() || 'M'}
            </div>
            <div style={styles.memberDetails}>
              <span style={styles.memberName}>{member?.name}</span>
              <span style={styles.memberEmail}>{member?.email}</span>
              {member?.distance_miles && (
                <span style={styles.memberDistance}>
                  {member.distance_miles} miles from store
                </span>
              )}
            </div>
            <button style={styles.logoutButton} onClick={handleLogout}>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </ClubLayout>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return '#ffc107';
    case 'processing': return '#2196f3';
    case 'ready': return '#4caf50';
    case 'picked_up': return '#9e9e9e';
    case 'cancelled': return '#f44336';
    default: return '#9e9e9e';
  }
};

const styles: { [key: string]: React.CSSProperties } = {
  content: {
    padding: '1rem',
    paddingBottom: '5rem', // Space for bottom nav
    maxWidth: '600px',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#666',
    fontSize: '1.125rem',
  },
  section: {
    marginBottom: '1.5rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#333',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  viewAllLink: {
    fontSize: '0.875rem',
    color: '#508590',
    textDecoration: 'none',
    fontWeight: 500,
  },
  alertCard: {
    padding: '1rem',
    borderRadius: '8px',
    borderLeft: '4px solid',
    marginBottom: '0.75rem',
  },
  alertHeader: {
    marginBottom: '0.5rem',
  },
  alertType: {
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  alertMessage: {
    margin: '0 0 0.5rem 0',
    fontSize: '0.875rem',
    color: '#333',
    lineHeight: '1.4',
  },
  alertAnimals: {
    fontSize: '0.75rem',
    color: '#666',
  },
  // Locker Pickup Card Styles
  lockerPickupCard: {
    backgroundColor: '#f3e8ff',
    border: '2px solid #7c3aed',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  lockerPickupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  lockerPickupIcon: {
    fontSize: '1.5rem',
  },
  lockerPickupTitle: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: '#7c3aed',
    letterSpacing: '0.05em',
  },
  lockerPickupBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
    marginBottom: '1rem',
  },
  lockerInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  lockerLabel: {
    fontSize: '0.875rem',
    color: '#6b21a8',
  },
  lockerNumber: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1f2937',
  },
  passcodeBox: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.25rem',
    textAlign: 'center' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.5rem',
  },
  passcodeLabel: {
    fontSize: '0.75rem',
    color: '#6b7280',
    letterSpacing: '0.1em',
    fontWeight: 500,
  },
  passcodeValue: {
    fontSize: '2.5rem',
    fontWeight: 700,
    fontFamily: 'monospace',
    color: '#7c3aed',
    letterSpacing: '0.2em',
  },
  passcodeNote: {
    fontSize: '0.75rem',
    color: '#9ca3af',
  },
  lockerPickupFooter: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    marginBottom: '1rem',
  },
  lockerOrderNumber: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#374151',
  },
  lockerOrderItems: {
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  directionsButton: {
    display: 'block',
    backgroundColor: '#7c3aed',
    color: 'white',
    textAlign: 'center' as const,
    padding: '0.875rem',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  reminderCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  reminderInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  reminderProduct: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.9375rem',
  },
  reminderDays: {
    fontSize: '0.8125rem',
    color: '#E73C3E',
  },
  reorderButton: {
    backgroundColor: '#508590',
    color: '#fff',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  myCardButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    backgroundColor: '#C41E24',
    color: '#fff',
    padding: '1rem 1.25rem',
    borderRadius: '12px',
    textDecoration: 'none',
    marginBottom: '1.5rem',
    boxShadow: '0 2px 8px rgba(196, 30, 36, 0.3)',
  },
  myCardIcon: {
    fontSize: '2rem',
  },
  myCardText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  myCardTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  myCardSubtitle: {
    fontSize: '0.8125rem',
    opacity: 0.9,
  },
  quickActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  actionCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: '#fff',
    padding: '1rem 0.5rem',
    borderRadius: '12px',
    textDecoration: 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  actionIcon: {
    fontSize: '1.75rem',
  },
  actionLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#333',
  },
  orderCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
  orderInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  orderNumber: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.9375rem',
  },
  orderDate: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  orderRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '0.25rem',
  },
  orderStatus: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  orderTotal: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.9375rem',
  },
  memberInfo: {
    marginTop: '1rem',
  },
  memberCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  memberAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#508590',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  memberDetails: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  memberName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.9375rem',
  },
  memberEmail: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  memberDistance: {
    fontSize: '0.75rem',
    color: '#508590',
  },
  logoutButton: {
    backgroundColor: 'transparent',
    color: '#E73C3E',
    border: '1px solid #E73C3E',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
};

export default ClubDashboard;
